<template>
  <div class="city-selector-wrapper">
    <div
      :class="containerClasses"
      :tabindex="isDisabled || isForeignCountry ? -1 : 0"
      @click="onToggleDropdown"
      @keydown.enter.prevent="onToggleDropdown"
      @keydown.space.prevent="onToggleDropdown"
      @keydown.esc="onCloseDropdown"
      @keydown.down.prevent="navigateOptions(1)"
      @keydown.up.prevent="navigateOptions(-1)"
    >
      <input
        ref="inputElement"
        class="city-selector__input"
        :placeholder="placeholder"
        :disabled="isDisabled || isForeignCountry"
        :value="displayValue"
        @input="onSearchInput"
        @focus="onInputFocus"
        @blur="onInputBlur"
        @click.stop="onOpenDropdown"
      />
      <SpinnerLoader
        v-if="isLoadingCities"
        class="city-selector__loader"
      />
      <slot v-else />
    </div>

    <transition name="dropdown">
      <div
        v-if="isDropdownOpen"
        class="city-selector__dropdown"
        role="listbox"
      >
        <div
          v-for="(option, index) in citiesList"
          :key="option.id"
          :class="optionClasses(index)"
          role="option"
          :aria-selected="isSelected(option)"
          @click="onSelectOption(option, index)"
          @mouseenter="focusedIndex = index"
        >
          <p class="city-selector__info-text">
            {{ option.name_full_name_ua }}
          </p>
        </div>
        <div
          v-if="!citiesList || citiesList.length === 0"
          class="city-selector__empty-state"
        >
          {{ emptyStateText }}
        </div>
      </div>
    </transition>

    <div
      v-if="hasValidationErrors"
      class="city-selector__error-container"
      role="alert"
    >
      <div v-for="(error, idx) in validationErrors" :key="idx">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { SpinnerLoader } from '@/shared/ui/loader';

const CLIENT_SEARCH_DELAY = 1000;

export default {
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    value: {
      type: [String, Number, Object],
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isForeignCountry: {
      type: Boolean,
      default: false,
    },
    foreignCountryLabel: {
      type: String,
      default: 'Іноземна країна',
    },
    emptyStateText: {
      type: String,
      default: 'No options available',
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isDropdownOpen: false,
      isInputFocused: false,
      citiesList: null,
      searchInput: '',
      focusedIndex: -1,
      selectedIndex: -1,
      searchTimeoutId: null,
      validationErrors: this.errorMessages || [],
      isInternalChange: false,
    };
  },
  mounted() {
    this.updateCitiesList();
    document.addEventListener('click', this.onDocumentClick);
  },
  beforeDestroy() {
    this.clearAllTimeouts();
    document.removeEventListener('click', this.onDocumentClick);
  },
  components: { SpinnerLoader },
  computed: {
    ...mapGetters('cityModule', ['isLoadingCities', 'preloadCitiesList']),
    getCitiesList() {
      return this.citiesList;
    },
    containerClasses() {
      return {
        'city-selector': true,
        'city-selector--disabled': this.isDisabled || this.isForeignCountry,
        'city-selector--error': this.hasValidationErrors,
        'city-selector--open': this.isDropdownOpen,
      };
    },
    hasValidationErrors() {
      return this.validationErrors.length > 0;
    },
    displayValue() {
      if (this.isForeignCountry) {
        return this.foreignCountryLabel;
      }

      if (this.searchInput && this.selectedIndex === -1) {
        return this.searchInput;
      }

      if (!this.value) {
        return '';
      }

      if (this.isLoadingCities) {
        return '';
      }

      return this.getOptionLabel(this.value) || '';
    },
  },
  methods: {
    ...mapActions('cityModule', ['searchCityByName', 'searchCityById']),
    isSameId(id1, id2) {
      if (id1 === id2) return true;
      return String(id1) === String(id2);
    },
    getIdFromValue(value) {
      if (value === null || value === undefined) return null;
      return typeof value === 'object' ? value?.id : value;
    },
    needsFullLoad(val) {
      if (val === null || val === undefined) return false;
      if (typeof val === 'string' || typeof val === 'number') return true;
      if (typeof val === 'object' && val.id !== null && val.id !== undefined) {
        return !val.name_full_name_ua && !val.nameFullNameUa;
      }
      return false;
    },
    findAndSetSelectedIndex() {
      if (!this.value || !this.citiesList?.length) {
        return false;
      }

      const valueId = this.getIdFromValue(this.value);
      if (valueId === null) {
        return false;
      }

      const index = this.citiesList.findIndex((city) =>
        this.isSameId(city.id, valueId)
      );

      if (index !== -1) {
        this.selectedIndex = index;
        return true;
      }

      return false;
    },
    updateCitiesList() {
      if (this.isForeignCountry) {
        this.citiesList = [
          { id: 0, name: 'іншій країні', name_full_name_ua: this.foreignCountryLabel, zone: 0 },
        ];
      } else {
        this.citiesList = this.preloadCitiesList;
      }
    },
    optionClasses(index) {
      return {
        'city-selector__option': true,
        'is-focused': index === this.focusedIndex,
        'is-selected': this.isSelected(this.citiesList[index]),
      };
    },
    onToggleDropdown() {
      if (this.isDisabled || this.isForeignCountry || this.isLoadingCities) return;
      this.isDropdownOpen ? this.onCloseDropdown() : this.onOpenDropdown();
    },
    onOpenDropdown() {
      if (this.isDisabled || (!this.citiesList?.length && !this.searchInput)) return;
      this.isDropdownOpen = true;
      this.$nextTick(() => {
        this.$refs.inputElement?.focus();
        this.isInputFocused = true;
      });
    },
    onCloseDropdown() {
      this.isDropdownOpen = false;
      this.focusedIndex = -1;
    },
    onSearchInput(event) {
      this.searchInput = event.target.value;
    },
    onDocumentClick(event) {
      if (!this.$el.contains(event.target)) {
        this.onCloseDropdown();
      }
    },
    onInputFocus() {
      this.isInputFocused = true;
    },
    onInputBlur() {
      this.isInputFocused = false;
      this.onCloseDropdown();
    },
    onSelectOption(option, index) {
      this.$emit('beforeChange', option, this.value);

      this.selectedIndex = index;
      this.searchInput = '';
      this.isInternalChange = true;
      this.$emit('input', option);
      this.$emit('change', option);
      this.onCloseDropdown();
      this.$nextTick(() => {
        this.isInternalChange = false;
      });
    },
    navigateOptions(direction) {
      if (!this.isDropdownOpen) {
        this.onOpenDropdown();
        return;
      }

      if (!this.citiesList?.length) return;

      const maxIndex = this.citiesList.length - 1;
      let newIndex = this.focusedIndex + direction;

      if (newIndex < 0) newIndex = 0;
      if (newIndex > maxIndex) newIndex = maxIndex;

      this.focusedIndex = newIndex;

      if (this.focusedIndex >= 0 && this.focusedIndex <= maxIndex) {
        this.onSelectOption(this.citiesList[this.focusedIndex], this.focusedIndex);
      }
    },
    clearAllTimeouts() {
      if (this.searchTimeoutId) {
        clearTimeout(this.searchTimeoutId);
      }
    },
    getOptionLabel(option) {
      if (typeof option !== 'object' || !option) return '';
      return option?.name_full_name_ua || option?.nameFullNameUa || null;
    },
    isSelected(option) {
      if (!this.value || !option) return false;
      return this.isSameId(option.id, this.getIdFromValue(this.value));
    },
    scheduleClientSearch(input) {
      this.clearAllTimeouts();

      if (input.length === 0) {
        this.validationErrors = [...this.errorMessages];
        return;
      }

      this.searchTimeoutId = setTimeout(async () => {
        this.validationErrors = [];
        this.onCloseDropdown();
        this.citiesList = await this.searchCityByName(input);
        this.searchTimeoutId = null;
      }, CLIENT_SEARCH_DELAY);
    },
    async loadFullCityIfNeeded(val) {
      if (!this.needsFullLoad(val)) return;

      const id = typeof val === 'object' ? val.id : val;
      const fullCity = await this.searchCityById(id);

      if (fullCity) {
        this.isInternalChange = true;
        this.$emit('input', fullCity);
        this.$emit('change', fullCity);
        this.$nextTick(() => {
          this.isInternalChange = false;
        });
      }
    },
  },
  watch: {
    value: {
      handler(newVal) {
        if (!this.isInternalChange) {
          this.loadFullCityIfNeeded(newVal);
        }
        this.findAndSetSelectedIndex();
      },
      immediate: true,
    },
    citiesList: {
      handler() {
        this.findAndSetSelectedIndex();
        if (this.isInputFocused && !this.hasValidationErrors && !this.isDropdownOpen) {
          this.onOpenDropdown();
        }
      },
      immediate: true,
    },
    searchInput(newValue) {
      this.selectedIndex = -1;
      this.focusedIndex = -1;
      if (newValue.length > 0) {
        this.citiesList = null;
      }
      this.scheduleClientSearch(newValue);
    },
    isForeignCountry(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.updateCitiesList();
        this.searchInput = '';
        this.focusedIndex = -1;

        if (newValue) {
          const foreignOption = {
            id: 0,
            name: 'іншій країні',
            name_full_name_ua: this.foreignCountryLabel,
            zone: 0,
          };
          this.$emit('input', foreignOption);
          this.$emit('change', foreignOption);
          this.selectedIndex = 0;
        } else {
          this.$nextTick(() => {
            const firstCity = this.preloadCitiesList && this.preloadCitiesList[0];
            if (firstCity) {
              this.citiesList = this.preloadCitiesList;
              const found = this.findAndSetSelectedIndex();
              if (!found) {
                const currentId = this.getIdFromValue(this.value);
                if (!currentId) {
                  this.selectedIndex = 0;
                  this.$emit('input', firstCity);
                  this.$emit('change', firstCity);
                } else {
                  this.selectedIndex = -1;
                }
              }
            } else {
              const currentId = this.getIdFromValue(this.value);
              if (!currentId) {
                this.selectedIndex = -1;
                this.$emit('input', null);
                this.$emit('change', null);
              }
            }
          });
        }
      }
    },
    preloadCitiesList(newList) {
      if (!this.isForeignCountry && newList && newList.length) {
        this.citiesList = newList;

        const found = this.findAndSetSelectedIndex();

        if (!found && this.selectedIndex === -1) {
          this.selectedIndex = 0;
          this.$emit('input', newList[0]);
          this.$emit('change', newList[0]);
        }
      }
    },
    clientsError(newValue) {
      this.validationErrors = newValue ? [newValue] : [];
    },
    errorMessages(newValue) {
      this.validationErrors = newValue || [];
    },
  },
};
</script>

<style lang="scss" scoped>
.city-selector-wrapper {
  position: relative;
  width: 100%;
}

.city-selector {
  position: relative;
  background-color: #ffffff;
  border: 1px solid #E4E4E7;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 4px;
  width: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &--disabled {
    background-color: #F6F6F9;
    cursor: not-allowed;
    opacity: 0.6;
  }

  &--error {
    border-color: #ef4444;
  }

  &--open {
    border-color: #E4E4E7;
  }

  &__input {
    font-family: inherit;
    font-size: 14px;
    color: #09090B;
    background: transparent;
    border: none !important;
    outline: none;
    flex: 1;
    cursor: text;
    min-width: 0;

    &::placeholder {
      color: #D4D4D8;
    }

    &:hover {
      border: none !important;
    }

    &:disabled {
      cursor: not-allowed;
    }
  }

  &__icon {
    width: 20px;
    height: 20px;
    margin-left: 8px;
    flex-shrink: 0;
  }

  &__loader {
    width: 20px;
    margin-left: 8px;
    flex-shrink: 0;
  }

  &__dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    max-height: 300px;
    overflow-y: auto;
    background-color: #ffffff;
    border: 1px solid #E4E4E7;
    border-radius: 6px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
    z-index: 1000;
    margin-top: 4px;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 3px;

      &:hover {
        background: #9ca3af;
      }
    }
  }

  &__option {
    padding: 10px 16px;
    cursor: pointer;
    transition: background-color 0.15s ease;
    font-size: 14px;
    color: #09090B;

    &:hover,
    &.is-focused {
      background-color: #f3f4f6;
    }

    &.is-selected {
      background-color: #eff6ff;
      color: #FB923C;
    }
  }

  &__empty-state {
    padding: 16px;
    text-align: center;
    color: #9ca3af;
    font-size: 14px;
  }

  &__info-text {
    margin: 0;

    &:not(:last-child) {
      margin-bottom: 4px;
    }

    strong {
      font-weight: 600;
    }
  }

  &__error-container {
    font-size: 12px;
    color: #ef4444;
    margin-top: 4px;
  }
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dropdown-enter,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
